<template>
  <Modal v-bind="$attrs" v-on="$listeners" @closed="closed">
    <div class="overflow-hidden rounded main-height bg-white">
      <div class="main-height overflow-auto">
        <!-- 検索 -->
        <div class="flex justify-end items-center mb-3 mt-1">
          <!-- 年度検索 -->
          <div class="mr-2 flex items-end">
            <input
              type="number"
              name="filterYear"
              min="2024"
              max="2999"
              placeholder="年度 検索"
              class="py-2 border border-gray-400 rounded-md leading-5 focus:bg-white focus:text-gray-900 font-bold w-20 text-center"
              v-model="filter.year"
            />
            <span class="pl-1 pr-2 font-bold text-gray-500 text-sm">年度</span>
          </div>

          <!-- 期 -->
          <div class="mr-2 font-bold text-gray-500">
            <span class="text-4xl">{{ term }}</span>
            <span class="pl-1 pr-2 text-sm">期</span>
          </div>

          <!-- 切替 -->
          <div class="border border-gray-400 rounded mr-5">
            <div class="flex items-center py-1 rounded mx-5 ">
              <div v-for="tab in typeList"
                :key="tab.id"
                class="w-28 text-center mx-1 cursor-pointer"
                :class="[type == tab.id ? 'bg-light-blue-700' : 'hover:bg-gray-400', 'bg-gray-300 rounded-md py-1 px-3 text-sm font-medium text-white']"
                @click="typeChanged(tab)"
              >
                {{ tab.title }}
              </div>
            </div>
          </div>

          <!-- ユーザー検索 -->
          <div class="mr-5">
            <div v-if="userName" class="font-bold bg-white rounded py-1.5 px-2">
              {{ userName }}
            </div>
            <WmsSuggestInput
              v-else
              placeholder="名前 検索"
              :clearable="true"
              :selectionItems="staffList"
              displayField="userName"
              v-model="filter.user"
              class="staff-input"
            >
            </WmsSuggestInput>
          </div>

          <!-- 検索ボタン -->
          <PrimaryButton
            text="検索"
            class="button-contents mr-5 w-32"
            @click="getData()"
          >
          </PrimaryButton>
        </div>

        <div class="m-3 flex justify-between">
          <div class="self-center">※始末書・反省文詳細をクリックするとメインタスクが開きます</div>

          <div v-if="!userName" class="text-gray-700 font-bold mx-1 self-center">
            {{ list.length }}人
          </div>

          <!-- CSV -->
          <vue-json-to-csv :json-data="csvData" :csv-title="'始末書集計'">
            <PrimaryButton text="CSV出力" :buttonContents="'要'" class="mr-1 bg-yellow-600">
              <Icon v-if="!loading" slot="before" iconName="Download" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
              <LoadingIcon v-else slot="before" class="h-4 w-4 my-auto ml-2 text-white" />
            </PrimaryButton>
          </vue-json-to-csv>
        </div>

        <table v-if="list && list.length" class="overflow-auto main table-auto mb-3 w-full">
          <thead class="bg-white">
            <tr class="border border-gray-300">
              <th v-for="(c, index) in columns" :key="index" scope="col" class="pl-4 pr-1 py-3 text-xs font-medium text-gray-500 tracking-wider bg-colu" :class="c.class">
                {{c.title}}
              </th>
            </tr>
          </thead>

          <tr v-for="(row, i) in list" :key="i" class="border border-b-2 border-gray-300 tracking-wider hover-color text-gray-700">

            <!-- ユーザー -->
            <td class="py-0.5 px-3">
              {{ row.userName }}
            </td>
            <!-- 始末書件数 -->
            <td class="py-0.5 px-3 text-center font-bold text-blue-700">
              {{ row.apologyCount }} 枚
            </td>
            <!-- 始末書詳細 -->
            <td class="py-0.5 px-3 text-white font-bold text-xs">
              <div v-for="(d, j) in row.apologyDetails" :key="i+'a'+j" class="p-2 rounded bg-blue-700 m-2 cursor-pointer" @click="toMainTask(d.mainId)">
                <p class="border-b border-white">No. {{ d.requestNo }}</p>
                <p>{{ d.inquiry_title }}</p>
              </div>
            </td>
            <!-- 反省文件数 -->
            <td class="py-0.5 px-3 text-center border-l font-bold text-green-700">
              {{ row.reflectCount }} 枚
            </td>
            <!-- 反省文詳細 -->
            <td class="py-0.5 px-3 text-white font-bold text-xs">
              <div v-for="(d, j) in row.reflectDetails" :key="i+'r'+j" class="p-2 rounded bg-green-700 m-2 cursor-pointer" @click="toMainTask(d.mainId)">
                <p class="border-b border-white">No. {{ d.requestNo }}</p>
                <p>{{ d.inquiry_title }}</p>
              </div>
            </td>
          </tr>
        </table>
        <div v-else-if="!loading && (!list || !list.length)" class="flex justify-center items-center text-gray-500 font-bold">
          <p class="text-gray-400 font-bold">対象データがありません</p>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from '@components/Modal.vue'
import Icon from '@components/Icon.vue'
import LoadingIcon from '@assets/loading-circle.svg'
import PrimaryButton from '@components/PrimaryButton.vue'
import WmsSuggestInput from '@wmscomponents/WmsSuggestInput.vue'
import VueJsonToCsv from 'vue-json-to-csv'
import * as dialogs from '@libs/dialogs'
import * as backend from '@libs/backend'
import * as moment from 'moment'
import * as utils from '@libs/utils'
import { FOUNDED } from '@/config'

export default {
  components: {
    Modal,
    Icon,
    LoadingIcon,
    PrimaryButton,
    WmsSuggestInput,
    VueJsonToCsv
  },

  props: ['open', 'userId', 'userName', 'bonusYear', 'bonusPeriod'],

  data() {
    return {
      // ローディング
      loading: false,
      // 検索
      filter: {
        year: null,
        // 対象者絞込み
        user: '',
        userId: []
      },
      // 切替ボタン
      typeList: [
        { id: 1, title: '上期' },
        { id: 2, title: '下期' }
      ],
      type: 1,
      // カラム
      columns: [
        {title: '名前'},
        {title: '始末書枚数'},
        {title: '始末書詳細'},
        {title: '反省文枚数', class: 'border-l'},
        {title: '反省文詳細'},
      ],
      // リスト
      list: [
        { toStaffName: '', apologyCount: 0, apologyDetails: [{ requestNo: '', inquiry_title: '' }], reflectCount: 0, reflectDetails: [{ requestNo: '', inquiry_title: '' }] }
      ],
      // 期
      term: null,
      reload: 0
    }
  },

  created() {
    // 初期値設定（2～7月は上期、8～1月は下期だが、ひと月前の期をデフォルトにする）
    const month = Number(moment().format('MM'))
    let year = Number(moment().format('YYYY'))
    // 2月以下の場合はマイナス1年
    if (month <= 2) {
      year--
    }
    this.filter.year = year

    // 上期・下期
    if (month >= 3 && month <= 8) {
      this.type = 1
    } else {
      this.type = 2
    }
  },

  computed: {
    staffList() {
      return this.$store.getters.userList
        .filter(row => row.user_name && !row.user_name.includes('未定'))
        .map(row => ({ userId: row.id, userName: utils.deleteKana(row.user_name || '') }))
    },

    csvData() {
      const res = []
      if (this.list && this.list.length) {
        for (let i = 0; i < this.list.length; i++) {
          const r = this.list[i]
          let length = 0
          if (r.apologyCount > r.reflectCount) {
            length = r.apologyCount
          } else {
            length = r.reflectCount
          }

          for (let j = 0; j < length; j++) {
            const row = { 名前: r.userName, 始末書: '', 反省文: '' }
            const apo = r.apologyDetails[j]
            const ref = r.reflectDetails[j]

            if (apo) {
              row['始末書'] = 'No.' + apo.requestNo + '：' + apo.inquiry_title
            }

            if (ref) {
              row['反省文'] = 'No.' + ref.requestNo + '：' + ref.inquiry_title
            }
            res.push(row)
          }
        }
      }
      return res
    }
  },

  watch: {
    open() {
      if (this.open) {
        // 賞与画面から開いた場合
        if (this.userId) {
          this.filter.userId = [String(this.userId)]
          if (this.bonusYear && this.bonusPeriod) {
            this.filter.year = this.bonusYear
            this.type = this.bonusPeriod
          }
        } else {
          this.filter.userId = null
        }

        this.getData()
      }
    }
  },

  methods: {
    /**
     * 初期にリセット
     */
    closed() {
      this.loading = false
      this.$emit('close')
    },

    /**
     * リスト取得
     */
    async getData() {
      this.loading = true
      this.list = []

      if (!this.filter.year) {
        await dialogs.showErrorDialog('「年」を入力してください', '検索対象の「年」が未入力です。')
        return
      }

      this.term = Number(this.filter.year) - FOUNDED

      let startDay
      let endDay

      // 上期
      if (this.type == 1) {
        startDay = this.filter.year + '-02-01'
      // 下期
      } else if (this.type == 2) {
        startDay = this.filter.year + '-08-01'
      }

      endDay = moment(startDay + 'T00:00:00').add(5, 'months').endOf('month').format('YYYY-MM-DD')

      const res = await backend.postData('evaluation/getApologyList', { ...this.filter, startDay, endDay })
      if (res.data.data && res.data.data.length) {
        res.data.data.forEach(row => {
          row.userName = utils.deleteKana(row.toStaffName)
        })
        this.list = res.data.data
      }
      this.loading = false
    },

    /**
     * 切替イベント
     * @paran tab 選択したタブ
     */
    async typeChanged(tab) {
      this.type = tab.id
      await this.getData()
    },

    /**
     * メインタスクOPEN
     * @param id メインタスクID
     */
    toMainTask(id) {
      const resolvedRoute = this.$router.resolve({
        name: 'RequestPost',
        query: {
          mode: 1, requestPostId: id
        }
      })
      window.open(resolvedRoute.href, '_blank')
    },
  }
}
</script>
<style scoped>
.main-height {
  height: 85vh;
}

.main {
  min-width: 700px;
}

.bg-colu {
  background: #edecf7;
}

.hover-color:hover {
  background: #edecf7 !important;
}

.staff-input >>> input[type=text] {
  height: 39px;
  margin-top: -2px;
  border-radius: 0.375rem !important;
  border-color: rgb(146, 145, 145) !important;
  width: 150px !important;
}
</style>